/**
 * Smart Shikka Secure Mobile PWA.
 *
 * Responsive, bilingual (English + Hindi) installable PWA screens:
 * Login, Pair device, Chat, Pending approvals, Approve/reject,
 * Action status/result, Devices, Revoke, Emergency Stop, Offline/error.
 *
 * Security notes:
 * - The device session token lives only in React state (memory).
 * - It is never written to localStorage, sessionStorage, URL, or logs.
 * - QR contains only an opaque requestId + HTTPS origin; the pairing code is
 *   entered separately by the user.
 * - Chat profiles are logical only; the server selects provider/model/API key.
 */
import { useEffect, useState, type FormEvent, useCallback } from "react";
import { api } from "../api.js";
import { useLocale, LocaleToggle, ThemeToggle } from "../ui.js";
import { MobileCommandPanel } from "../components/MobileCommandPanel.js";
import { safeErrorCode, safeErrorText } from "@smart-shikka/react";

type View = "login" | "pair" | "chat" | "approvals" | "result" | "devices" | "stop";

interface ChatMessage {
  role: "user" | "assistant";
  text: string;
}

export function MobileAppPage() {
  const { tr, locale } = useLocale();
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && !navigator.onLine);
  const [ownerToken, setOwnerToken] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [emergencyStopped, setEmergencyStopped] = useState(false);
  const [deviceToken, setDeviceToken] = useState<string | null>(null);
  const [deviceId, setDeviceId] = useState<string | null>(null);
  const [view, setView] = useState<View>("login");
  const [error, setError] = useState<string | null>(null);
  const [initialRequestId, setInitialRequestId] = useState<string>("");
  const isCeoEligible = role === "super-admin" || role === "super-sub-admin";

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // Deep-link QR pairing: parse ?pair=<requestId> or ?requestId=<requestId> from the hash.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const raw = window.location.hash.replace(/^#\/?/, "");
    const [base, query] = raw.split("?");
    if (base === "mobile" && query) {
      const params = new URLSearchParams(query);
      const pair = params.get("pair") ?? params.get("requestId");
      if (pair) {
        setInitialRequestId(pair);
        setView("pair");
      }
    }
  }, []);

  // Resolve authenticated role and global Emergency Stop state once logged in.
  useEffect(() => {
    if (!ownerToken) return;
    let alive = true;
    const refresh = async () => {
      const [session, health] = await Promise.all([
        api.session(ownerToken),
        api.mobileHealth(),
      ]);
      if (!alive) return;
      if (session.ok && session.data?.role) setRole(session.data.role);
      if (health.ok) setEmergencyStopped(health.data?.emergencyStop ?? false);
    };
    void refresh();
    const id = setInterval(refresh, 10000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [ownerToken]);

  return (
    <main className="ss-login">
      <div className="ss-login-form-wrap">
        <div className="ss-login-card" style={{ maxWidth: 520, width: "100%" }}>
          <div className="ss-login-toprow">
            <ThemeToggle />
            <LocaleToggle />
          </div>
          <h2>{tr("mobile")}</h2>
          {offline ? <div className="msg error">{tr("mobileOffline")}</div> : null}
          {error ? (
            <div className="msg error">
              {safeErrorCode(error) === error
                ? `${safeErrorText(error, locale === "hi" ? "hi" : "en").title} (${error})`
                : error}
            </div>
          ) : null}
          {emergencyStopped ? (
            <div className="msg error" style={{ textAlign: "center" }}>
              {tr("mobileEmergencyStopped")}
            </div>
          ) : null}
          {ownerToken ? (
            <NavTabs view={view} setView={setView} deviceToken={deviceToken} />
          ) : null}
          {view === "login" && (
            <LoginView
              onLogin={(token) => { setOwnerToken(token); setView("pair"); }}
              setError={setError}
            />
          )}
          {view === "pair" && ownerToken && (
            <PairView
              ownerToken={ownerToken}
              initialRequestId={initialRequestId}
              onPaired={(token, id) => { setDeviceToken(token); setDeviceId(id); setView("chat"); }}
              setError={setError}
            />
          )}
          {view === "chat" && deviceToken && (
            <ChatView deviceToken={deviceToken} setError={setError} emergencyStopped={emergencyStopped} isCeoEligible={isCeoEligible} />
          )}
          {view === "approvals" && ownerToken && (
            <ApprovalsView
              ownerToken={ownerToken}
              ownDeviceId={deviceId}
              setError={setError}
            />
          )}
          {view === "result" && deviceToken && (
            <ResultView deviceToken={deviceToken} setError={setError} />
          )}
          {view === "devices" && ownerToken && (
            <DevicesView
              ownerToken={ownerToken}
              setError={setError}
              onRevoked={() => setView("pair")}
            />
          )}
          {view === "stop" && ownerToken && (
            <StopView ownerToken={ownerToken} setError={setError} canResume={isCeoEligible} emergencyStopped={emergencyStopped} />
          )}
          {deviceToken ? (
            <p className="muted" style={{ fontSize: 12, marginTop: 12 }}>
              {tr("mobileTokenMemory")}
            </p>
          ) : null}
          <p className="muted" style={{ fontSize: 12 }}>{tr("mobileNoSecret")}</p>
        </div>
      </div>
    </main>
  );
}

function NavTabs({ view, setView, deviceToken }: { view: View; setView: (v: View) => void; deviceToken: string | null }) {
  const { tr } = useLocale();
  const tabs: { key: View; label: string; needsDevice: boolean }[] = [
    { key: "chat", label: tr("mobileChat"), needsDevice: true },
    { key: "approvals", label: tr("mobileApprovals"), needsDevice: false },
    { key: "result", label: tr("mobileResult"), needsDevice: true },
    { key: "devices", label: tr("mobileDevices"), needsDevice: false },
    { key: "stop", label: tr("mobileStop"), needsDevice: false },
  ];
  return (
    <div className="jarvis-links" style={{ flexWrap: "wrap", marginBottom: 12 }}>
      {tabs.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`badge ${view === t.key ? "ok" : ""}`}
          disabled={t.needsDevice && !deviceToken}
          onClick={() => setView(t.key)}
          style={{ opacity: t.needsDevice && !deviceToken ? 0.4 : 1 }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function LoginView({ onLogin, setError }: { onLogin: (token: string) => void; setError: (e: string | null) => void }) {
  const { tr } = useLocale();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const res = await api.login(email.trim(), password);
    setBusy(false);
    if (res.ok && res.data?.session?.token) {
      onLogin(res.data.session.token);
      return;
    }
    setError(tr("invalidCreds"));
  }

  return (
    <form onSubmit={submit}>
      <p className="sub">{tr("loginSub")}</p>
      <div className="ss-field">
        <label htmlFor="m-email">{tr("email")}</label>
        <input id="m-email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
      </div>
      <div className="ss-field">
        <label htmlFor="m-password">{tr("password")}</label>
        <input id="m-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
      </div>
      <button className="ss-login-submit" type="submit" disabled={busy}>
        {tr("login")}
      </button>
    </form>
  );
}

function PairView({ ownerToken, initialRequestId, onPaired, setError }: { ownerToken: string; initialRequestId?: string; onPaired: (token: string, id: string) => void; setError: (e: string | null) => void }) {
  const { tr } = useLocale();
  const [requestId, setRequestId] = useState(initialRequestId ?? "");
  const [oneTimeCode, setOneTimeCode] = useState("");
  const [busy, setBusy] = useState(false);

  function parseQrUrl(value: string) {
    try {
      const text = value.trim();
      if (!text) return;
      const match = text.match(/[#?&](?:pair|requestId)=([^&\s]+)/);
      if (match?.[1]) {
        setRequestId(decodeURIComponent(match[1]));
      } else if (/^req-/.test(text)) {
        setRequestId(text);
      }
    } catch {
      // ignore malformed paste
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!requestId.trim() || !oneTimeCode.trim()) {
      setError(tr("genericError"));
      return;
    }
    setBusy(true);
    setError(null);
    const publicKey = await api.provisionDeviceKeyPair();
    const res = await api.mobilePairingComplete(ownerToken, requestId.trim(), oneTimeCode.trim(), publicKey ?? undefined);
    setBusy(false);
    if (res.ok && res.data?.sessionToken && res.data?.device?.deviceId) {
      onPaired(res.data.sessionToken, res.data.device.deviceId);
      return;
    }
    if (!res.ok) api.clearDeviceKeyPair();
    setError(res.error ? safeErrorCode(res.error) : tr("mobilePairFailed"));
  }

  return (
    <form onSubmit={submit}>
      <p className="sub">{tr("mobilePairDesc")}</p>
      <div className="ss-field">
        <label htmlFor="m-qr">{tr("mobileQrPaste")}</label>
        <input id="m-qr" placeholder={tr("mobileQrPasteHint")} onPaste={(e) => { parseQrUrl(e.clipboardData.getData("text")); }} onChange={(e) => parseQrUrl(e.target.value)} />
      </div>
      <div className="ss-field">
        <label htmlFor="m-req">{tr("mobilePairCode")} ({"requestId"})</label>
        <input id="m-req" value={requestId} onChange={(e) => setRequestId(e.target.value)} required />
      </div>
      <div className="ss-field">
        <label htmlFor="m-otc">{tr("code")}</label>
        <input id="m-otc" value={oneTimeCode} onChange={(e) => setOneTimeCode(e.target.value)} required autoComplete="one-time-code" />
      </div>
      <button className="ss-login-submit" type="submit" disabled={busy}>
        {tr("mobilePair")}
      </button>
    </form>
  );
}

function ChatView({
  deviceToken,
  setError,
  emergencyStopped,
  isCeoEligible,
}: {
  deviceToken: string;
  setError: (e: string | null) => void;
  emergencyStopped: boolean;
  isCeoEligible: boolean;
}) {
  const { tr } = useLocale();
  const [profile, setProfile] = useState<"fast-assistant" | "core-reasoning">("fast-assistant");
  const [message, setMessage] = useState("");
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!message.trim()) return;
    const text = message.trim();
    setHistory((h) => [...h, { role: "user", text }]);
    setMessage("");
    setBusy(true);
    setError(null);
    const res = await api.mobileChat(deviceToken, profile, text);
    setBusy(false);
    if (res.ok && res.data?.reply) {
      setHistory((h) => [...h, { role: "assistant", text: res.data!.reply }]);
    } else {
      setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
      setHistory((h) => [...h, { role: "assistant", text: `⚠ ${tr("genericError")}` }]);
    }
  }

  return (
    <div>
      <p className="sub">{tr("mobileChatDesc")}</p>
      <div className="ss-field">
        <label>{tr("status")}</label>
        <select className="ss-input" value={profile} onChange={(e) => setProfile(e.target.value as "fast-assistant" | "core-reasoning")}>
          <option value="fast-assistant">{tr("mobileProfileFast")}</option>
          <option value="core-reasoning">{tr("mobileProfileCore")}</option>
        </select>
      </div>
      <div style={{ maxHeight: 320, overflow: "auto", border: "1px solid var(--border)", borderRadius: 8, padding: 8, marginBottom: 12, background: "var(--surface)" }}>
        {history.length === 0 ? <p className="muted">{tr("emptyList")}</p> : null}
        {history.map((m, i) => (
          <div key={i} style={{ marginBottom: 8, textAlign: m.role === "user" ? "right" : "left" }}>
            <span style={{ display: "inline-block", padding: "6px 10px", borderRadius: 12, background: m.role === "user" ? "var(--tertiary)" : "var(--card)", color: "var(--text)", fontSize: 14, whiteSpace: "pre-wrap" }}>
              {m.text}
            </span>
          </div>
        ))}
      </div>
      <form onSubmit={submit}>
        <div className="ss-field" style={{ display: "flex", gap: 8 }}>
          <input
            style={{ flex: 1 }}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={tr("mobileMessage")}
            disabled={busy}
          />
          <button className="ss-login-submit" type="submit" disabled={busy || !message.trim()}>
            {tr("mobileSend")}
          </button>
        </div>
      </form>
      {isCeoEligible ? (
        <MobileCommandPanel deviceToken={deviceToken} emergencyStopped={emergencyStopped} />
      ) : (
        <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          {tr("mobileCeoOnlyCommands")}
        </p>
      )}
    </div>
  );
}

function ApprovalsView({ ownerToken, ownDeviceId, setError }: { ownerToken: string; ownDeviceId: string | null; setError: (e: string | null) => void }) {
  const { tr } = useLocale();
  const [pending, setPending] = useState<unknown[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await api.mobilePairingPending(ownerToken);
    if (res.ok && Array.isArray(res.data?.actions)) {
      setPending(res.data.actions);
    } else {
      setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
    }
  }, [ownerToken, setError, tr]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function decide(requestId: string, confirmed: boolean) {
    setBusy(true);
    const res = await api.mobilePairingConfirm(ownerToken, requestId, confirmed, ownDeviceId ?? undefined);
    setBusy(false);
    if (res.ok) void refresh();
    else setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
  }

  return (
    <div>
      {pending.length === 0 ? <p className="muted">{tr("emptyList")}</p> : null}
      {pending.map((a: any) => {
        const self = a.deviceId === ownDeviceId;
        return (
          <div key={a.requestId} className="jarvis-help" style={{ marginBottom: 8 }}>
            <div className="row"><strong>{a.actionId}</strong><span className="badge">{a.status}</span></div>
            <div className="row muted" style={{ fontSize: 12 }}>{a.deviceId}</div>
            {self ? (
              <p className="muted" style={{ fontSize: 12 }}>{tr("mobileOwnerConfirm")}</p>
            ) : (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button className="ss-login-submit" style={{ background: "var(--ready)" }} disabled={busy} onClick={() => decide(a.requestId, true)}>{tr("mobileApprove")}</button>
                <button className="ss-login-submit" style={{ background: "var(--blocked)" }} disabled={busy} onClick={() => decide(a.requestId, false)}>{tr("mobileReject")}</button>
              </div>
            )}
          </div>
        );
      })}
      <button className="ss-login-submit" onClick={refresh}>{tr("status")}</button>
    </div>
  );
}

function ResultView({ deviceToken, setError }: { deviceToken: string; setError: (e: string | null) => void }) {
  const { tr } = useLocale();
  const [requestId, setRequestId] = useState("");
  const [result, setResult] = useState<Record<string, unknown> | null>(null);
  const [busy, setBusy] = useState(false);

  async function fetchResult() {
    if (!requestId.trim()) return;
    setBusy(true);
    setError(null);
    const res = await api.mobileActionResult(deviceToken, requestId.trim());
    setBusy(false);
    if (res.ok) setResult(res.data as Record<string, unknown>);
    else setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
  }

  return (
    <div>
      <div className="ss-field" style={{ display: "flex", gap: 8 }}>
        <input value={requestId} onChange={(e) => setRequestId(e.target.value)} placeholder="requestId" />
        <button className="ss-login-submit" onClick={fetchResult} disabled={busy}>{tr("mobileResult")}</button>
      </div>
      {result ? <pre style={{ fontSize: 12, overflow: "auto", maxHeight: 240, background: "var(--card)", padding: 8, borderRadius: 8 }}>{JSON.stringify(result, null, 2)}</pre> : null}
    </div>
  );
}

function DevicesView({ ownerToken, setError, onRevoked }: { ownerToken: string; setError: (e: string | null) => void; onRevoked: () => void }) {
  const { tr } = useLocale();
  const [devices, setDevices] = useState<unknown[]>([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const res = await api.mobileDevices(ownerToken);
    if (res.ok && Array.isArray(res.data?.devices)) setDevices(res.data.devices);
    else setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
  }, [ownerToken, setError, tr]);

  useEffect(() => { void refresh(); }, [refresh]);

  async function revoke(deviceId: string, all = false) {
    setBusy(true);
    const res = all ? await api.mobileRevokeAll(ownerToken) : await api.mobileRevoke(ownerToken, deviceId);
    setBusy(false);
    if (res.ok) {
      if (all) onRevoked();
      void refresh();
    } else {
      setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
    }
  }

  return (
    <div>
      {devices.length === 0 ? <p className="muted">{tr("emptyList")}</p> : null}
      {devices.map((d: any) => (
        <div key={d.deviceId} className="jarvis-help" style={{ marginBottom: 8 }}>
          <div className="row"><strong>{d.deviceName}</strong><span className="badge">{d.status}</span></div>
          <div className="row muted" style={{ fontSize: 12 }}>{d.deviceId}</div>
          <button className="ss-login-submit" style={{ background: "var(--blocked)", marginTop: 8 }} disabled={busy} onClick={() => revoke(d.deviceId)}>
            {tr("mobileRevoke")}
          </button>
        </div>
      ))}
      <button className="ss-login-submit" style={{ background: "var(--blocked)" }} disabled={busy} onClick={() => revoke("", true)}>
        {tr("mobileRevokeAll")}
      </button>
    </div>
  );
}

function StopView({
  ownerToken,
  setError,
  canResume,
  emergencyStopped,
}: {
  ownerToken: string;
  setError: (e: string | null) => void;
  canResume: boolean;
  emergencyStopped: boolean;
}) {
  const { tr } = useLocale();
  const [config, setConfig] = useState<{ emergencyStop?: boolean } | null>(null);

  useEffect(() => {
    void (async () => {
      const res = await api.jarvisConfig(ownerToken);
      if (res.ok) setConfig(res.data?.config ?? { emergencyStop: false });
    })();
  }, [ownerToken]);

  async function toggle() {
    if (!canResume) return;
    const res = await api.jarvisEmergencyStop(ownerToken);
    if (res.ok) setConfig(res.data?.config ?? { emergencyStop: true });
    else setError(res.error ? safeErrorCode(res.error) : tr("genericError"));
  }

  const active = config?.emergencyStop || emergencyStopped;
  return (
    <div className="msg error" style={{ textAlign: "center" }}>
      <h3>{tr("mobileStop")}</h3>
      {active ? <p>{tr("mobileStopActive")}</p> : <p>{tr("ownerStopIdle")}</p>}
      <button
        className="ss-login-submit"
        style={{ background: "var(--blocked)", opacity: canResume ? 1 : 0.5 }}
        onClick={toggle}
        disabled={!canResume}
      >
        {active ? tr("resumeShikkaCeo") : tr("ownerStopButton")}
      </button>
      {!canResume ? <p className="muted" style={{ fontSize: 12 }}>{tr("mobileCeoOnlyStop")}</p> : null}
    </div>
  );
}
